// ============================================================================
// Inventory Categories Modal Component
// ============================================================================
// Description: CRUD modal for managing inventory categories (Database, Backend, etc.)
// Features: List, Add, Edit, Delete categories with validation
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';
import { InventoryCategory, InventoryCategoryForm } from '../../../types/inventory';
import realInventoryService from '../../../services/inventoryService';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { confirmDelete, swalError } from '../../common/confirm';

interface InventoryCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InventoryCategoriesModal: React.FC<InventoryCategoriesModalProps> = ({
  isOpen,
  onClose
}) => {
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<InventoryCategory | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<InventoryCategoryForm>({
    category_code: '',
    category_name: '',
    category_description: '',
    display_order: 0,
    is_active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load categories when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await realInventoryService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: InventoryCategory) => {
    setEditingCategory(category);
    setFormData({
      category_code: category.category_code,
      category_name: category.category_name,
      category_description: category.category_description || '',
      display_order: category.display_order,
      is_active: category.is_active
    });
    setShowForm(true);
    setErrors({});
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      category_code: '',
      category_name: '',
      category_description: '',
      display_order: categories.length + 1,
      is_active: true
    });
    setShowForm(true);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.category_code.trim()) {
      newErrors.category_code = 'Category code is required';
    } else if (!/^[A-Z_]+$/.test(formData.category_code)) {
      newErrors.category_code = 'Category code must be uppercase letters and underscores only';
    } else if (categories.some(c => c.category_code === formData.category_code && c.id !== editingCategory?.id)) {
      newErrors.category_code = 'Category code must be unique';
    }

    if (!formData.category_name.trim()) {
      newErrors.category_name = 'Category name is required';
    }

    if (formData.display_order < 0) {
      newErrors.display_order = 'Display order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      if (editingCategory) {
        await realInventoryService.updateCategory(editingCategory.id, formData);
      } else {
        await realInventoryService.createCategory({
          ...formData,
          created_by: 1 // Mock user ID
        });
      }

      await loadCategories();
      setShowForm(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Failed to save category:', error);
      swalError('Failed to save category. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category: InventoryCategory) => {
    const ok = await confirmDelete({ title: `Delete category "${category.category_name}"?` });
    if (!ok) return;
    try {
      setLoading(true);
      await realInventoryService.deleteCategory(category.id);
      await loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      swalError('Failed to delete category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingCategory(null);
    setErrors({});
    onClose();
  };

  const handleBackClick = () => {
    setShowForm(false);
    setEditingCategory(null);
    setErrors({});
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Inventory Categories Management</Modal.Header>
      <Modal.Body className="!p-0 overflow-hidden">
        <div className="modal-content">
          {!showForm ? (
            // Categories List View
            <div className="list-view">
              <div className="list-header">
                <div className="list-title">
                  <h3>Categories ({categories.length})</h3>
                  <p>Manage high-level inventory classifications</p>
                </div>
                <button className="add-button" onClick={handleAdd} disabled={loading} data-testid="BTN_inventory_cat_add">
                  <span>➕</span>Add Category
                </button>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading categories...</p>
                </div>
              ) : (
                <div className="items-grid">
                  {categories.map(category => (
                    <div key={category.id} className="item-card">
                      {/* Header Row: Code + Name */}
                      <div className="data_card_header">
                        <div className="code-name-row">
                          <span className="item-code">{category.category_code || 'NO-CODE'}</span>
                          <span className="item-name">{category.category_name || 'NO-NAME'}</span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="data_default">
                        {category.category_description || 'No description provided'}
                      </div>

                      {/* Categories and Status Tags */}
                      <div className="data_tags">
                        <span>Status: {category.is_active ? 'Active' : 'Inactive'}</span>
                        <span> • </span>
                        <span>Order: {category.display_order}</span>
                        <span> • </span>
                        <span>Created: {new Date(category.created_at).toLocaleDateString()}</span>
                      </div>

                      {/* Footer with Actions */}
                      <div className="data_card_footer">
                        <div className="data_paths">
                          🎯 Category: {category.category_code}
                        </div>
                        <div style={{display: 'flex', gap: '8px', marginLeft: 'auto'}}>
                          <button
                            className="edit-button"
                            onClick={() => handleEdit(category)}
                            disabled={loading}
                            data-testid={`BTN_inventory_cat_row_${category.id}_edit`}
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDelete(category)}
                            disabled={loading}
                            data-testid={`BTN_inventory_cat_row_${category.id}_delete`}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {categories.length === 0 && !loading && (
                <div className="empty-state">
                  <div className="empty-icon">📂</div>
                  <h3>No categories found</h3>
                  <p>Create your first inventory category to get started.</p>
                  <button className="create-button" onClick={handleAdd} data-testid="BTN_inventory_cat_create_empty">
                    Create Category
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Add/Edit Form View
            <div className="form-view">
              <div className="form-header">
                <h3>
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h3>
                <button
                  className="back-button"
                  onClick={handleBackClick}
                  data-testid="BTN_inventory_cat_back"
                >
                  ← Back to List
                </button>
              </div>

              <form onSubmit={handleSubmit} className="category-form" data-testid="MOD_inventory_cat_form">
                <div className="form-grid">
                  <div className="form-group">
                    <FormControl.Input
                      label="Category Code"
                      required
                      id="category_code"
                      data-testid="INP_inventory_cat_code"
                      value={formData.category_code}
                      onChange={(e) => setFormData({
                        ...formData,
                        category_code: e.target.value.toUpperCase()
                      })}
                      placeholder="e.g., DATABASE, BACKEND"
                      error={errors.category_code}
                      disabled={submitting}
                      helperText="Uppercase letters and underscores only"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Category Name"
                      required
                      id="category_name"
                      data-testid="INP_inventory_cat_name"
                      value={formData.category_name}
                      onChange={(e) => setFormData({
                        ...formData,
                        category_name: e.target.value
                      })}
                      placeholder="e.g., Database Objects"
                      error={errors.category_name}
                      disabled={submitting}
                    />
                  </div>

                  <div className="form-group full-width">
                    <FormControl.Textarea
                      label="Description"
                      id="category_description"
                      data-testid="INP_inventory_cat_description"
                      value={formData.category_description}
                      onChange={(e) => setFormData({
                        ...formData,
                        category_description: e.target.value
                      })}
                      placeholder="Describe the purpose and scope of this category"
                      rows={3}
                      disabled={submitting}
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Display Order"
                      required
                      id="display_order"
                      type="number"
                      data-testid="INP_inventory_cat_display_order"
                      value={formData.display_order}
                      onChange={(e) => setFormData({
                        ...formData,
                        display_order: parseInt(e.target.value) || 0
                      })}
                      min={0}
                      error={errors.display_order}
                      disabled={submitting}
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Checkbox
                      label="Active Category"
                      checked={formData.is_active}
                      onChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      disabled={submitting}
                      data-testid="CHK_inventory_cat_is_active"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <Button variant="outline" type="button" onClick={handleBackClick} data-testid="BTN_inventory_cat_cancel">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant={editingCategory ? 'update' : 'fill'}
                    icon={editingCategory ? Check : Save}
                    disabled={submitting}
                    data-testid="BTN_inventory_cat_submit"
                  >
                    {submitting ? (editingCategory ? 'Updating...' : 'Submitting...') : editingCategory ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default InventoryCategoriesModal;
